import { ColumnMatcher } from "./ColumnMatcher";
import Coordinates, { CoordinateData } from "./Coordinates";
import {
  ColumnHighlightObject,
  BoxHighlightObject,
  HighlightObjectModified,
  SpanHighlightObject,
} from "./HighlightObject";
import { ColumnSection } from "./matchers/ColumnSection";
import { PageSection } from "./matchers/PageSection";
import { Section } from "./matchers/Section";
import Span from "./Span";

class HighlightObjectFactory {
  static createRowHighlight(
    id: string,
    color: string,
    rowSpans: Span[],
    pageHeight: number,
    visible: boolean,
  ): BoxHighlightObject {
    // const rowCoordinates =
    // find corners of row
    const y0 = rowSpans
      .map((span) => span.y0)
      .reduce(
        (prev, curr) => (prev <= curr ? prev : curr),
        Number.MAX_SAFE_INTEGER,
      );
    const y1 = rowSpans
      .map((span) => span.y1)
      .reduce((prev, curr) => (prev <= curr ? curr : prev), 0);

    return {
      id,
      color,
      opacity: 0.5,
      type: "BOX",
      draggable: false,
      topYPercent: (y0 / pageHeight) * 100,
      bottomYPercent: (y1 / pageHeight) * 100,
      leftXPercent: 0,
      rightXPercent: 100,
      objectMoved: () => null,
      visible: visible,
    };
    // return this.createSpanHighlight(id, color, span, visible, false);
  }

  static createExtractHighlight(
    id: string,
    color: string,
    span: Coordinates,
    visible: boolean,
  ): SpanHighlightObject {
    return this.createSpanHighlight(id, color, span, visible, false);
  }

  static createReuseHighlight(
    id: string,
    color: string,
    span: Coordinates,
    visible: boolean,
  ): SpanHighlightObject {
    return this.createSpanHighlight(id, color, span, visible, true);
  }

  static createColumnHighlight(
    id: string,
    color: string,
    columnModel: ColumnMatcher,
    objectMoved: (newCoordinates: HighlightObjectModified) => void,
  ): ColumnHighlightObject {
    return this.createColumnHighlightExact(
      id,
      color,
      columnModel.columnSection.leftXPercent,
      columnModel.columnSection.rightXPercent,
      columnModel.showColumn,
      objectMoved,
    );
  }
  static createColumnHighlightExact(
    id: string,
    color: string,
    leftXPercent: number,
    rightXPercent: number,
    columnShowColumn: boolean,
    objectMoved: (newCoordinates: HighlightObjectModified) => void,
  ): ColumnHighlightObject {
    return {
      id,
      color,
      opacity: 0.5,
      leftXPercent: leftXPercent,
      rightXPercent: rightXPercent,
      visible: leftXPercent < rightXPercent && columnShowColumn,
      type: "COLUMN",
      objectMoved,
    };
  }

  static createSectionHighlight(
    id: string,
    color: string,
    section: Section,
    objectMoved: (newCoordinates: HighlightObjectModified) => void,
    interactable: boolean = false,
  ): BoxHighlightObject {
    switch (section.discriminator) {
      case "PAGE": {
        return this.createPageSectionHighlight(
          id,
          color,
          section as PageSection,
          objectMoved,
          interactable,
        );
      }
      default: {
        throw new Error(
          `Cannot create Highlight Object for Section with discriminator ${section.discriminator}`,
        );
      }
    }
  }

  private static createSpanHighlight(
    id: string,
    color: string,
    span: Coordinates,
    visible: boolean,
    bordered: boolean,
  ): SpanHighlightObject {
    return {
      id,
      color,
      opacity: 0.5,
      visible,
      type: "SPAN",
      height: span.height,
      width: span.width,
      x: span.x,
      y: span.y,
      pageIndex: span.pageIndex,
      objectMoved: () => null,
      bordered: bordered,
    };
  }

  private static createPageSectionHighlight(
    id: string,
    color: string,
    pageSection: PageSection,
    objectMoved: (newCoordinates: HighlightObjectModified) => void,
    interactable: boolean,
  ): BoxHighlightObject {
    return {
      type: "BOX",
      id,
      color,
      opacity: 0.5,
      topYPercent: pageSection.topLeft.y,
      leftXPercent: pageSection.topLeft.x,
      rightXPercent: pageSection.bottomRight.x,
      bottomYPercent: pageSection.bottomRight.y,
      visible: true,
      draggable: interactable,
      objectMoved,
    };
  }
}
export { HighlightObjectFactory };
